import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import {
  withoutForbiddenElevation,
  withUtcDateFields,
} from '@application/data-access';
import { fromWireEnums } from '../entities/academy-enums.model';
import { RecordingsQuery } from '../entities/academy-query.model';
import {
  LiveEventCreateRequest,
  LiveEventMetricsResponse,
  LiveEventResponse,
  LiveEventUpdateRequest,
} from '../entities/academy.model';
import {
  AttendanceItem,
  ChatHistoryItem,
  CoverResponse,
  LiveIngestResponse,
  MineResult,
  PlaybackTokenResponse,
  SubscriberItem,
  toMineResult,
} from '../entities/academy-responses.model';
import { AcademyCrudService } from './academy-crud.service';

/**
 * HTTP client for the Lives area custom endpoints (`/LiveEvents/*`).
 * Source of truth: `docs/academy-api-integration.md` §7.1. PascalCase, enums by name.
 * Base `LiveEvents` create goes through `AcademyCrudService`; OData reads (grids/list)
 * use `AcademyODataService` from the stores.
 */
@Injectable({ providedIn: 'root' })
export class LiveEventsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;
  private readonly crud = inject(AcademyCrudService);

  /**
   * `GET /LiveEvents/recordings` — recordings visible to the viewer's audience
   * (Recorded + Immediate visibility, ordered by EndedOnUtc desc). Plain JSON array.
   */
  public recordings(
    query: RecordingsQuery = {}
  ): Observable<LiveEventResponse[]> {
    return this.http
      .get<LiveEventResponse[]>(`${this.baseUrl}/LiveEvents/recordings`, {
        params: this.buildRecordingsParams(query),
        withCredentials: true,
      })
      .pipe(map((items) => fromWireEnums(items)));
  }

  /**
   * `POST /LiveEvents/{id}/playbackToken` — signed CDN token for the recording.
   * 🔑 identity from token: sends an empty body, never a `PersonId`. ⚠ 403 outside audience —
   * marked `withoutForbiddenElevation()` because no supervisor login puts the tenant in the
   * audience, and the dock/widget ask for this token with no user action behind it.
   */
  public playbackToken(liveEventId: string): Observable<PlaybackTokenResponse> {
    return this.http.post<PlaybackTokenResponse>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(
        liveEventId
      )}/playbackToken`,
      {},
      { context: withoutForbiddenElevation(), withCredentials: true }
    );
  }

  /** `POST /LiveEvents` — create a live event (generic OData create, status-agnostic). */
  public create(
    viewModel: LiveEventCreateRequest
  ): Observable<LiveEventResponse> {
    return this.crud.create<LiveEventResponse>('LiveEvents', viewModel);
  }

  /**
   * `GET /LiveEvents?id={guid}` — read ONE event by key. Carries every column the edit
   * builder needs (audience, products, `CoverUrl`), so a deep-link can prefill from it.
   * The shared OData list also returns `CoverUrl` (measured in prod 2026-09-15) but is
   * projected to 7 columns via `$select`, so it lacks the audience/product fields.
   */
  public getById(liveEventId: string): Observable<LiveEventResponse | null> {
    return this.crud.getById<LiveEventResponse>('LiveEvents', liveEventId);
  }

  /** `PATCH /LiveEvents?id={guid}` — update a Scheduled event's metadata (edit mode). */
  public update(
    liveEventId: string,
    viewModel: LiveEventUpdateRequest
  ): Observable<LiveEventResponse> {
    return this.crud.update<LiveEventResponse>(
      'LiveEvents',
      liveEventId,
      viewModel
    );
  }

  /**
   * `POST /LiveEvents/{id}/cover` — multipart cover image (field `file`). The API forwards
   * the binary to the StorageService and answers with the stored `CoverUrl`. The browser
   * sets the multipart boundary; the interceptor skips `Content-Type` for `FormData`.
   * Mirrors the course/path cover upload.
   */
  public setCover(liveEventId: string, file: File): Observable<CoverResponse> {
    const form = new FormData();
    form.append('file', file); // File carries its own name → multipart filename is preserved
    return this.http.post<CoverResponse>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(liveEventId)}/cover`,
      form,
      { withCredentials: true }
    );
  }

  /**
   * `POST /LiveEvents/{id}/cancel` — cancel a Scheduled/Live event.
   * ⚠ 409 when not in a cancellable state — surfaced raw; the consumer maps the "conflict" copy.
   */
  public cancel(liveEventId: string, reason?: string): Observable<void> {
    let params = new HttpParams();
    if (reason) params = params.set('reason', reason);
    return this.http.post<void>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(liveEventId)}/cancel`,
      {},
      { params, withCredentials: true }
    );
  }

  /**
   * `DELETE /LiveEvents?id={guid}` — soft-delete an event (`GenericController.RemoveAsync`).
   * The backend gates on the producer only, with no status check: the caller decides which
   * statuses may be deleted. ⚠ 403 not producer.
   */
  public remove(liveEventId: string): Observable<void> {
    return this.crud.delete('LiveEvents', liveEventId);
  }

  /**
   * `GET /LiveEvents/{id}/ingest` — owner-only WHIP publish URL (browser ingest).
   * Fetch per session, never store: the URL is a publish secret. ⚠ 403 not owner ·
   * 404 missing / event created without a WHIP URL · 409 not publishable (Ended/Recorded/Cancelled).
   */
  public ingest(liveEventId: string): Observable<LiveIngestResponse> {
    return this.http.get<LiveIngestResponse>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(liveEventId)}/ingest`,
      { withCredentials: true }
    );
  }

  /** `GET /LiveEvents/{id}/metrics` — aggregated producer metrics. ⚠404. */
  public metrics(liveEventId: string): Observable<LiveEventMetricsResponse> {
    return this.http.get<LiveEventMetricsResponse>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(liveEventId)}/metrics`,
      { withCredentials: true }
    );
  }

  /**
   * `GET /LiveEvents/{id}/subscribers` — producer-view enrolled people (§8.1).
   * Server-side `search` (by name), `top`/`skip` paging. Wire is a PascalCase
   * `{ Items, Total }` envelope, folded into `MineResult`. ⚠ 403 not owner · 404 not visible.
   */
  public subscribers(
    liveEventId: string,
    query: { search?: string; top?: number; skip?: number } = {}
  ): Observable<MineResult<SubscriberItem>> {
    let params = new HttpParams()
      .set('top', String(query.top ?? 20))
      .set('skip', String(query.skip ?? 0));
    if (query.search) params = params.set('search', query.search);
    return this.http
      .get<unknown>(
        `${this.baseUrl}/LiveEvents/${encodeURIComponent(
          liveEventId
        )}/subscribers`,
        {
          params,
          // `SubscribedOn` é instante UTC mas o nome não termina em `Utc`, logo
          // a regra do sufixo não o apanha: declarado aqui, o `DateInterceptor`
          // converte-o e a grade formata-o sem tratamento especial.
          context: withUtcDateFields(['SubscribedOn']),
          withCredentials: true,
        }
      )
      .pipe(map((res) => toMineResult<SubscriberItem>(res)));
  }

  /**
   * `GET /LiveEvents/{id}/attendances` — producer-view live attendance roster (backend 013),
   * cross-tenant, open sessions first. Server-side `search` (by name) and `top`/`skip`
   * paging (`top` above 200 answers 422). Wire is a PascalCase `{ Items, Total }` envelope,
   * folded into `MineResult`; every date field ends in `Utc`, so the `DateInterceptor`
   * converts them without extra declarations. ⚠ 403 not producer · 404 not found.
   */
  public attendances(
    liveEventId: string,
    query: { search?: string; top?: number; skip?: number } = {}
  ): Observable<MineResult<AttendanceItem>> {
    let params = new HttpParams()
      .set('top', String(query.top ?? 20))
      .set('skip', String(query.skip ?? 0));
    if (query.search) params = params.set('search', query.search);
    return this.http
      .get<unknown>(
        `${this.baseUrl}/LiveEvents/${encodeURIComponent(
          liveEventId
        )}/attendances`,
        { params, withCredentials: true }
      )
      .pipe(map((res) => toMineResult<AttendanceItem>(res)));
  }

  /**
   * `GET /LiveEvents/{id}/chat` — producer-view chat history (backend 013c/d): all tenants,
   * newest first, messages removed by moderation flagged `IsRemoved` with an empty `Text`.
   * `top`/`skip` paging (server default 50; above 200 answers 422). Wire is a PascalCase
   * `{ Items, Total }` envelope, folded into `MineResult`. ⚠ 403 not producer · 404 not found.
   */
  public chat(
    liveEventId: string,
    query: { top?: number; skip?: number } = {}
  ): Observable<MineResult<ChatHistoryItem>> {
    const params = new HttpParams()
      .set('top', String(query.top ?? 50))
      .set('skip', String(query.skip ?? 0));
    return this.http
      .get<unknown>(
        `${this.baseUrl}/LiveEvents/${encodeURIComponent(liveEventId)}/chat`,
        { params, withCredentials: true }
      )
      .pipe(map((res) => toMineResult<ChatHistoryItem>(res)));
  }

  private buildRecordingsParams(query: RecordingsQuery): HttpParams {
    let params = new HttpParams();
    // App scope is resolved server-side from the user's account (feature 010) — never send
    // `?product=`. `query.product` is kept on the type as accepted-but-ignored until the
    // admin manual filter UI is retired.
    if (query.search) params = params.set('search', query.search);
    if (query.from) params = params.set('from', query.from);
    if (query.to) params = params.set('to', query.to);
    return params;
  }
}
